import React, { useRef, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip, Typography, message, Alert, Button } from 'antd';
import styles from './RichTextSection.module.css';
import ContentSecurityModal from './ContentSecurityModal';

const { Text } = Typography;

/**
 * DOMPurify configuration for Quill editor
 * Allows only tags and attributes that Quill supports
 */
const QUILL_SAFE_CONFIG = {
  ALLOWED_TAGS: [
    // Text formatting
    'p', 'br', 'strong', 'em', 'u', 's', 'a', 'span',

    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',

    // Lists
    'ul', 'ol', 'li',

    // Structure
    'div', 'blockquote', 'pre', 'code',

    // Tables (for Excel paste)
    'table', 'thead', 'tbody', 'tr', 'th', 'td',

    // Images
    'img',
  ],

  ALLOWED_ATTR: [
    'class', 'style', 'href', 'src', 'alt', 'title',
    'target', 'rel', 'colspan', 'rowspan', 'align',
  ],

  // Security settings
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Analyze what types of content were removed during sanitization
 */
const analyzeRemovedContent = (original, cleaned) => {
  const removed = [];

  if (original.includes('<script')) removed.push('scripts');
  if (original.includes('<iframe')) removed.push('embedded frames');
  if (original.includes('onerror=') || original.includes('onclick=') ||
      original.includes('onload=') || original.includes('onmouse')) {
    removed.push('event handlers');
  }
  if (original.includes('<object') || original.includes('<embed')) {
    removed.push('embedded objects');
  }
  if (original.includes('javascript:')) removed.push('javascript URLs');

  return removed;
};

/**
 * Clean Excel-generated HTML tables
 * Removes inline styles and Excel-specific attributes
 */
const cleanExcelTable = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const table = doc.querySelector('table');

  if (!table) return htmlString;

  // Remove Excel-specific attributes
  table.removeAttribute('border');
  table.removeAttribute('cellspacing');
  table.removeAttribute('cellpadding');
  table.removeAttribute('width');

  // Remove inline styles and classes from all elements
  table.querySelectorAll('*').forEach(el => {
    el.removeAttribute('style');
    el.removeAttribute('class');
    el.removeAttribute('align');
    el.removeAttribute('valign');
  });

  // Add clean class for styling
  table.setAttribute('class', 'report-table');

  return table.outerHTML;
};

const RichTextSection = ({
  value,
  onChange,
  label,
  placeholder = 'Type here or paste content from Excel...',
  showTips = true,
  showInlineTip = false,
  minHeight = '150px',
  charLimit = null
}) => {
  const quillRef = useRef(null);
  const editorWrapperRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showSecurityBanner, setShowSecurityBanner] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [removedContentTypes, setRemovedContentTypes] = useState([]);

  // Handle clipboard paste for images and tables
  const handlePaste = (e) => {
    const clipboardData = e.clipboardData;

    // Check for image in clipboard (e.g., Excel chart screenshot)
    const items = clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();

        const blob = items[i].getAsFile();
        const reader = new FileReader();

        reader.onload = (event) => {
          const base64 = event.target.result;
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', base64);
            // Move cursor after image
            quill.setSelection(range.index + 1);
          }
        };

        reader.readAsDataURL(blob);
        return;
      }
    }

    // Get HTML content from clipboard
    const htmlData = clipboardData.getData('text/html');
    const plainText = clipboardData.getData('text/plain');

    // If there's HTML content, sanitize it
    if (htmlData) {
      e.preventDefault();

      // First, sanitize with DOMPurify for security
      const sanitized = DOMPurify.sanitize(htmlData, QUILL_SAFE_CONFIG);

      // Check if content was modified (security risk detected)
      const wasModified = htmlData.length !== sanitized.length;
      const removedTypes = analyzeRemovedContent(htmlData, sanitized);

      if (wasModified) {
        // Show toast notification
        const removedTypesText = removedTypes.length > 0
          ? removedTypes.join(', ')
          : 'unsupported content';

        message.warning({
          content: `Content security: Removed ${removedTypesText} for safety`,
          duration: 5,
        });

        // Show persistent banner
        setShowSecurityBanner(true);
        setRemovedContentTypes(removedTypes);

        // Log for debugging
        console.info('[Content Security] Sanitized pasted content:', {
          originalLength: htmlData.length,
          cleanedLength: sanitized.length,
          bytesRemoved: htmlData.length - sanitized.length,
          removedTypes: removedTypes,
          timestamp: new Date().toISOString(),
        });
      }

      // Handle tables specially (Excel paste)
      let finalHTML = sanitized;
      if (sanitized.includes('<table')) {
        finalHTML = cleanExcelTable(sanitized);
      }

      // Insert the sanitized content
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.clipboard.dangerouslyPasteHTML(range.index, finalHTML);
      }
    }
  };

  // Handle toolbar interactions to prevent blur when clicking toolbar
  useEffect(() => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;

    // Find the toolbar element
    const toolbar = wrapper.querySelector('.ql-toolbar');
    if (!toolbar) return;

    // Prevent blur when clicking on toolbar
    const handleToolbarMouseDown = (e) => {
      // Prevent the editor from losing focus when clicking toolbar
      e.preventDefault();

      // Keep the editor focused
      const quill = quillRef.current?.getEditor();
      if (quill) {
        // Re-focus the editor after a brief moment to allow the click to register
        setTimeout(() => {
          quill.focus();
        }, 0);
      }
    };

    toolbar.addEventListener('mousedown', handleToolbarMouseDown);

    return () => {
      toolbar.removeEventListener('mousedown', handleToolbarMouseDown);
    };
  }, []);

  // Add tooltips to toolbar buttons
  useEffect(() => {
    const tooltipConfig = {
      '.ql-header': 'Heading',
      '.ql-bold': 'Bold',
      '.ql-italic': 'Italic',
      '.ql-underline': 'Underline',
      '.ql-strike': 'Strikethrough',
      '.ql-list[value="ordered"]': 'Numbered list',
      '.ql-list[value="bullet"]': 'Bullet list',
      '.ql-indent[value="-1"]': 'Decrease indent',
      '.ql-indent[value="+1"]': 'Increase indent',
      '.ql-align': 'Text alignment',
      '.ql-link': 'Insert link',
      '.ql-image': 'Insert image',
      '.ql-clean': 'Clear formatting'
    };

    // Add tooltips to all toolbar buttons
    Object.entries(tooltipConfig).forEach(([selector, title]) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!el.getAttribute('title')) {
          el.setAttribute('title', title);
        }
      });
    });
  }, []);

  // Quill toolbar configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false // Preserve formatting from paste
    }
  };

  // Quill formats allowed
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image',
    'align'
  ];

  // Calculate character count from HTML content (strip tags)
  const getPlainTextLength = (html) => {
    if (!html) return 0;
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent.length;
  };

  const currentLength = getPlainTextLength(value);
  const isOverLimit = charLimit && currentLength > charLimit;

  return (
    <div className="rich-text-section">
      {label && (
        <div className={styles.sectionHeader}>
          <Text strong>{label}</Text>
          {charLimit && (
            <span className={isOverLimit ? styles.charCounterOverLimit : styles.charCounter}>
              ({currentLength}/{charLimit})
            </span>
          )}
          {showInlineTip && (
            <Text type="secondary" className={styles.inlineTip}>
              Tip: Use copy-paste to insert images, charts or tables
            </Text>
          )}
          {showTips && (
            <Tooltip title="Tips: Copy charts from Excel and paste directly. Copy table cells from Excel and paste to create formatted tables.">
              <InfoCircleOutlined className={styles.infoIcon} />
            </Tooltip>
          )}
        </div>
      )}

      {/* Content Security Banner */}
      {showSecurityBanner && (
        <Alert
          type="info"
          message="Content Security Active"
          description={
            <span>
              Unsupported content ({removedContentTypes.join(', ') || 'security risks'}) was removed from your paste.
              Your text and formatting are preserved. {' '}
              <Button
                type="link"
                size="small"
                onClick={() => setShowSecurityModal(true)}
                className={styles.learnMoreButton}
              >
                Learn more
              </Button>
            </span>
          }
          closable
          onClose={() => setShowSecurityBanner(false)}
          showIcon
          className={styles.alert}
        />
      )}

      <div
        ref={editorWrapperRef}
        onPaste={handlePaste}
        className={`rich-text-editor-wrapper ${isFocused ? 'focused' : ''} ${isFocused ? styles.editorWrapperFocused : styles.editorWrapper} ${styles.editorContainer}`}
        style={{ '--editor-min-height': minHeight }}
      >
        <ReactQuill
          ref={quillRef}
          value={value || ''}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          theme="snow"
        />
      </div>

      {/* Content Security Help Modal */}
      <ContentSecurityModal
        visible={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
      />

      <style>{`
        /* Compact and responsive rich text editor toolbar */
        .rich-text-editor-wrapper .ql-toolbar {
          display: var(--toolbar-display, none);
          border: 1px solid var(--rlc-neutral-300);
          border-bottom: none;
          background: var(--rlc-surface-subtle);
          padding: 4px;
          border-radius: 4px 4px 0 0;
          transition: all 0.2s ease-in-out;
        }

        /* Always show toolbar when focused */
        .rich-text-editor-wrapper.focused .ql-toolbar {
          display: flex !important;
        }

        /* Make toolbar buttons compact */
        .ql-toolbar .ql-formats {
          margin-right: 8px !important;
        }

        .ql-toolbar button {
          width: 28px !important;
          height: 28px !important;
          padding: 3px !important;
        }

        .ql-toolbar button svg {
          width: 16px !important;
          height: 16px !important;
        }

        .ql-toolbar .ql-picker {
          font-size: 13px !important;
        }

        .ql-toolbar .ql-picker-label {
          padding: 2px 4px !important;
          height: 28px !important;
        }

        /* Keep dropdown menus visible and prevent toolbar from hiding */
        .ql-toolbar .ql-picker.ql-expanded .ql-picker-options {
          z-index: 10;
          display: block !important;
        }

        /* Ensure toolbar stays visible when dropdown is open */
        .ql-toolbar .ql-picker.ql-expanded {
          z-index: 11;
        }

        /* Responsive toolbar - stack on small screens */
        @media (max-width: 768px) {
          .ql-toolbar {
            flex-wrap: wrap !important;
          }

          .ql-toolbar .ql-formats {
            margin-bottom: 4px;
          }
        }

        /* Editor container */
        .rich-text-editor-wrapper .ql-container {
          border: 1px solid var(--rlc-neutral-300);
          border-radius: 0 0 4px 4px;
          font-size: 14px;
          min-height: var(--editor-min-height, 150px);
        }

        .rich-text-editor-wrapper.focused .ql-container {
          border-color: var(--rlc-focus-ring);
          box-shadow: 0 0 0 2px rgba(6, 62, 68, 0.2);
        }

        /* Editor content */
        .rich-text-editor-wrapper .ql-editor {
          min-height: var(--editor-min-height, 150px);
          padding: 12px;
          text-align: left !important;
        }

        /* Ensure all paragraphs and content are left-aligned by default */
        .rich-text-editor-wrapper .ql-editor p,
        .rich-text-editor-wrapper .ql-editor div,
        .rich-text-editor-wrapper .ql-editor h1,
        .rich-text-editor-wrapper .ql-editor h2,
        .rich-text-editor-wrapper .ql-editor h3,
        .rich-text-editor-wrapper .ql-editor h4,
        .rich-text-editor-wrapper .ql-editor h5,
        .rich-text-editor-wrapper .ql-editor h6 {
          text-align: left;
        }

        /* Override only when explicitly set via align button */
        .rich-text-editor-wrapper .ql-editor .ql-align-center {
          text-align: center;
        }

        .rich-text-editor-wrapper .ql-editor .ql-align-right {
          text-align: right;
        }

        .rich-text-editor-wrapper .ql-editor .ql-align-justify {
          text-align: justify;
        }

        .ql-editor.ql-blank::before {
          color: #bfbfbf;
          font-style: normal;
          text-align: left !important;
        }

        /* Hover effects for toolbar buttons */
        .ql-toolbar button:hover,
        .ql-toolbar button:focus {
          color: var(--rlc-focus-ring) !important;
        }

        .ql-toolbar button.ql-active {
          color: var(--rlc-state-info) !important;
        }

        /* Tooltip styling */
        .ql-toolbar button[title]::after {
          content: attr(title);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.75);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 1000;
          margin-bottom: 4px;
        }

        .ql-toolbar button[title]:hover::after {
          opacity: 1;
        }
      `}</style>
    </div>
  );
};

export default RichTextSection;
